import {
  ChartBar,
  History,
  PlusCircle,
  Settings,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getSidebarHidden, getUserData } from "../../store/login.slice";
import { formatDateTime } from "../../helpers/formatters";
import styles from "./layoutWrapper.module.scss";

type NavItem = {
  label: string;
  route: string;
  icon: LucideIcon;
};

const NAV_ITEMS: NavItem[] = [
  { label: "Create Test", route: "/CreateTest", icon: PlusCircle },
  { label: "Complete Tests", route: "/completetests", icon: History },
  { label: "Incomplete Tests", route: "/incompletetests", icon: History },
  { label: "Performance", route: "/performance", icon: ChartBar },
  { label: "Reports", route: "/reports", icon: ChartBar },
  { label: "Account Settings", route: "/AccountSettings", icon: Settings },
];

const LayoutWrapper = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hidden = useSelector(getSidebarHidden);
  const userData = useSelector(getUserData);
  const [mobileDrawer, setMobileDrawer] = useState(false);

  const formattedDate = formatDateTime(
    userData.time_zone.value,
    new Date(userData.license_expiration),
  );

  // Close the drawer with Escape
  useEffect(() => {
    if (!mobileDrawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileDrawer(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileDrawer]);

  const isActive = (route: string) =>
    location.pathname.toLowerCase().includes(route.toLowerCase());

  const goTo = (route: string) => {
    navigate(route);
    setMobileDrawer(false);
  };

  const renderSidebar = () => (
    <div className={styles.sidebarContainer}>
      <h1 className={styles.sidebarLogoText}>
        OSCE <span className={styles.highlightedLogoText}>Dental</span>
      </h1>

      <nav className={styles.sidebarNavContainer}>
        {NAV_ITEMS.map(({ label, route, icon: Icon }) => (
          <button
            key={route}
            type="button"
            title={label}
            onClick={() => goTo(route)}
            className={`${styles.sidebarNavButton} ${
              isActive(route) ? styles.sidebarNavButtonSelected : ""
            }`}
          >
            <Icon className={styles.sidebarNavIcon} />
            {label}
          </button>
        ))}
      </nav>

      <div className={styles.expirationContainer}>
        <span className={styles.expirationLabel}>Expiration Date</span>
        <span className={styles.expirationDate}>{formattedDate}</span>
      </div>
    </div>
  );

  return (
    <div
      className={`${styles.appContainer} ${
        hidden ? styles.appContainerNoSidebar : ""
      }`}
    >
      {!hidden && (
        <>
          {/* Desktop sidebar */}
          <aside className={styles.desktopSidebarContainer}>
            {renderSidebar()}
          </aside>

          {/* Mobile burger button */}
          <button
            type="button"
            className={styles.mobileDrawerToggleButton}
            onClick={() => setMobileDrawer(true)}
            aria-label="Open navigation menu"
          >
            <div className={styles.mobileDrawerBurgerIcon} />
          </button>

          {/* Mobile drawer */}
          <div
            className={`${styles.mobileDrawerContainer} ${
              mobileDrawer ? styles.mobileDrawerOpen : ""
            }`}
            aria-hidden={!mobileDrawer}
          >
            <div
              className={styles.mobileDrawerBackdrop}
              onClick={() => setMobileDrawer(false)}
            />
            <div className={styles.mobileDrawerContentWrapper}>
              {renderSidebar()}
              <button
                type="button"
                className={styles.mobileDrawerCloseButton}
                onClick={() => setMobileDrawer(false)}
                aria-label="Close navigation menu"
              />
            </div>
          </div>
        </>
      )}

      <main className={styles.mainContent}>{children}</main>
    </div>
  );
};

export default LayoutWrapper;
